import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap, share, delay } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { OverlayLoaderService } from '../services/overlay-loader.service';
import { isNil } from 'lodash';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { Injectable, Injector } from '@angular/core';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private http: HttpClient,
    private overlayLoader: OverlayLoaderService,
    private matDialog: MatDialog,
    private injector: Injector,
    private authService: AuthService
  ) {}

  private _refresh$ = this.http.post<any>('auth/refresh', {}).pipe(delay(3000), share());

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => this.parseReponse(event, req)),
      catchError((error, caught) => {
        if (error instanceof HttpErrorResponse) {
          return this.handleResponseError(error, req, next);
        }
        return caught;
      })
    );
  }

  parseReponse(event: HttpEvent<any>, request: HttpRequest<any>) {
    if (
      event instanceof HttpResponse &&
      !isNil(event.url) &&
      (event.url.indexOf('/datapool') >= 0 || event.url.indexOf('/energyguard') >= 0 || event.url.indexOf('/socrates') >= 0) &&
      event.body
    ) {
      if (!isNil(event.headers) && event.headers.has('odata_query_count')) {
        return event.clone({
          body: {
            data: event.body,
            maxRowCount: parseInt(event.headers.get('odata_query_count') as string, 10),
          },
        });
      }

      if (this.requestUrlContainsMaxRowCount(request) || this.requestBodyContainsMaxRowCount(request)) {
        return event.clone({
          body: {
            data: event.body.data,
            maxRowCount: event.body.maxRowCount,
          },
        });
      }

      return event.clone({
        body: event.body.data,
      });
    }

    return event;
  }

  handleResponseError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler) {
    if (error.status === 503) {
      this.overlayLoader.isLoading = false;
      this.matDialog.open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Datenbank nicht erreichbar - versuchen Sie es später erneut',
          orientation: 'row',
          alignment: 'start',
        },
        panelClass: ['-md'],
      });
    } else if (error.status === 403 && error.headers.has('x-session-expired')) {
      this.overlayLoader.isLoading = false;
      this.matDialog
        .open(NotificationDialogComponent, {
          data: {
            descriptionText: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
            orientation: 'row',
            alignment: 'start',
          },
          panelClass: ['-md'],
        })
        .afterClosed()
        .subscribe(() => {
          const authService = this.injector.get(AuthService);
          authService.logout();
        });
    } else if (error.status === 401 && error.headers.has('x-token-expired')) {
      return this._refresh$.pipe(
        switchMap(() => {
          return next.handle(req).pipe(map((event: HttpEvent<any>) => this.parseReponse(event, req)));
        }),
        catchError((err, caught) => {
          const authService = this.injector.get(AuthService);
          authService.logout();
          return caught;
        })
      );
    }

    return throwError(() => error);
  }

  requestUrlContainsMaxRowCount(request: HttpRequest<any>): boolean {
    return (
      request.urlWithParams.toLowerCase().indexOf('returnmaxrowcountonly=true') >= 0 ||
      request.urlWithParams.toLowerCase().indexOf('returnmaxrowcount=true') >= 0
    );
  }

  requestBodyContainsMaxRowCount(request: HttpRequest<any>): boolean {
    return (
      !isNil(request.body) &&
      ((!isNil(request.body.returnmaxrowcountonly) && request.body.returnmaxrowcountonly) ||
        (!isNil(request.body.returnmaxrowcount) && request.body.returnmaxrowcount))
    );
  }
}
